import { Injectable, Compiler } from '@angular/core';
import { Router, NavigationStart, NavigationError, NavigationEnd, ActivatedRoute, Data, NavigationCancel } from '@angular/router';
import { SpinnerService } from './spinner/spinner.service';
import { TitleService } from './title.service';
import { AppearanceService, DEFAULT_THEME } from './appearance.service';
import { EupToastService } from './toastr/eupToast.service';
import { navigationErrorPrefix, paths } from '../../assets/consts';
import { TimberService } from '@logging/timber.service';
import { BiFeatureWrapperService } from '@logging/bi-feature-wrapper.service';
import { ApplicationNavigationService } from 'app/services/shell/application-navigation/application-navigation.service';
import { NavigationInfo } from '@shared/generalInterfaces';
import { Pages } from '@logging/interfaces/bi.enums';

@Injectable()
export class RouterInterceptorService {
	private prevUrl: string;
	private navigationStartTime: number;
	excludedPages: string[] = [Pages.Home, Pages.Login, Pages.LoginLegacy];

	get previousRoute() {
		if (this.prevUrl && this.prevUrl !== '/') {
			return this.prevUrl;
		}

		return this.applicationNavigationService.peekTransition()?.urlFrom;
	}

	constructor(
		private router: Router,
		private compiler: Compiler,
		private spinner: SpinnerService,
		private titleService: TitleService,
		private activatedRoute: ActivatedRoute,
		private appearanceService: AppearanceService,
		private eupToastr: EupToastService,
		private timberService: TimberService,
		private biFeatureWrapperService: BiFeatureWrapperService,
		private applicationNavigationService: ApplicationNavigationService
	) {}

	initialize(): void {
		this.router.events.subscribe((val: any) => {
			if (val instanceof NavigationStart) {
				this.handleNavigationStart(val);
			}

			if (val instanceof NavigationEnd) {
				this.handleNavigationEnd(val);
			}

			if (val instanceof NavigationError) {
				this.handleNavigationError(val);
			}

			if (val instanceof NavigationCancel) {
				this.handleNavigationCancel(val);
			}
		});
	}

	handleNavigationCancel(event: NavigationCancel) {
		this.timberService.trace('Navigation canceled while navigating to route', {
			module: 'RouterInterceptorService',
			extendedParameters: { Url: event.url, Reason: event.reason },
		});
		this.spinner.stop();
	}

	handleNavigationStart(event: NavigationStart) {
		this.prevUrl = this.router.url;
		this.navigationStartTime = new Date().getTime();
		this.spinner.start(true);
		this.timberService.trace('Navigation start navigating to route', {
			module: 'RouterInterceptorService',
			extendedParameters: { Url: event.url, NavigationTrigger: event.navigationTrigger, Id: `${event.id}` },
		});
		this.handleTransition(this.prevUrl, event.url);
	}

	handleNavigationEnd(event: NavigationEnd) {
		this.spinner.stop();
		this.eupToastr.clearAllToastrs();

		// calculate timing
		if (this.navigationStartTime) {
			const timeElapsed = new Date().getTime() - this.navigationStartTime;
		}

		let route = this.activatedRoute;

		while (route.firstChild) {
			route = route.firstChild;
		}
		route.data.subscribe((data: Data) => {
			if (!data.skipTitle) {
				this.titleService.setTitle(data.title);
			}

			const theme = data.theme || DEFAULT_THEME;

			this.appearanceService.toTheme(theme);
			this.appearanceService.setResponsive(data.isResponsive);
		});
		this.timberService.trace('Navigation end navigating to route', {
			module: 'RouterInterceptorService',
			extendedParameters: { Url: event.url, urlAfterRedirects: event.urlAfterRedirects, Id: `${event.id}` },
		});
	}

	handleNavigationError(event: NavigationError) {
		this.spinner.stop();
		this.timberService.error('Navigation error', {
			module: 'RouterInterceptorService',
			extendedParameters: { Url: event.url, Message: event.error, Id: `${event.id}` },
		});
	}

	private logBi(prevPage: string, newPage: string): void {
		this.timberService.uiNavigationEvent({
			sourceName: prevPage,
			targetName: newPage,
		});
	}

	private handleTransition(prevUrl: string, newUrl: string): void {
		const prevPage = this.biFeatureWrapperService.extractPageFromString(prevUrl);
		const newPage = this.biFeatureWrapperService.extractPageFromString(newUrl);
		const prevTransition = this.applicationNavigationService.peekTransition();
		const prev = prevTransition?.navigatedToAppId ?? prevPage.page;

		if ((prevPage.page && prevPage.page !== newPage.page) || prevTransition?.urlFrom.toLowerCase() === newUrl) {
			const isBackNavigation =
				prevTransition?.navigatedFromAppId === newPage.page || prevTransition?.urlFrom.toLowerCase() === newUrl;

			if (isBackNavigation) {
				this.logBi(prev, prevTransition.navigatedFromAppId);
				this.applicationNavigationService.popTransition();
			} else {
				this.logBi(prev, newPage.page);
				if (prevPage.wrapper === 'viewer-wrapper' && newPage.wrapper === 'viewer-wrapper') {
					this.replaceLastNavigation(newPage.page);
				} else if (this.shouldPushTransition(prev, newPage.page)) {
					const transition = {
						navigatedFromAppId: prev,
						navigatedToAppId: newPage.page,
						urlFrom: prevUrl,
					} as NavigationInfo;

					this.applicationNavigationService.pushTransition(transition);
				}
			}
		}
	}

	private replaceLastNavigation(newPage: string): void {
		const lastTransition = this.applicationNavigationService.popTransition();

		this.applicationNavigationService.pushTransition({
			navigatedFromAppId: lastTransition.navigatedFromAppId,
			navigatedToAppId: newPage,
			urlFrom: lastTransition.urlFrom,
		} as NavigationInfo);
	}

	private shouldPushTransition(prevPage: string, newPage: string): boolean {
		// on app load and refresh prevPage is undefined
		return prevPage && !this.excludedPages.includes(newPage);
	}
}
